<template>
  <div class="shop-page">
    <div v-if="loading" class="loading-screen">
      <div class="spinner">
        <div class="dot1"></div>
        <div class="dot2"></div>
      </div>
      <div class="loading-text">Загрузка магазина...</div>
      <svg width="0" height="0">
        <defs>
          <filter id="goo">
            <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="blur" />
            <feColorMatrix
              in="blur"
              values="
                1 0 0 0 0
                0 1 0 0 0
                0 0 1 0 0
                0 0 0 50 -8"
              result="goo"
            />
            <feBlend in="SourceGraphic" in2="goo" />
          </filter>
        </defs>
      </svg>
    </div>

    <div class="page-list" v-else>
      <!-- Диалог подтверждения покупки -->
      <transition name="fade">
        <div class="confirm-dialog-overlay" v-if="showConfirmDialog">
          <div class="confirm-dialog">
            <div class="confirm-dialog-header">
              <div class="confirm-dialog-icon">🎁</div>
              <h3>Подтверждение покупки</h3>
            </div>
            <div class="confirm-dialog-body">
              <p>Вы действительно хотите купить подарок?</p>
              <div class="confirm-dialog-gift">
                <div class="confirm-dialog-gift-name">{{ selectedGift?.name }}</div>
                <div class="confirm-dialog-gift-price">{{ selectedGift?.cost }} СПАСИБО</div>
              </div>
              <div v-if="selectedGift?.hasSize && getSizeComment(selectedGift.id)" class="confirm-dialog-size">
                Размер/цвет: {{ getSizeComment(selectedGift.id) }}
              </div>
            </div>
            <div class="confirm-dialog-footer">
              <button type="button" class="confirm-dialog-btn confirm-dialog-btn--cancel" @click="cancelPurchase">
                Отмена
              </button>
              <button type="button" class="confirm-dialog-btn confirm-dialog-btn--confirm" @click="confirmPurchase">
                Подтвердить
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Уведомление об успешной покупке -->
      <transition name="fade">
        <div class="success-notification" v-if="showSuccessNotification">
          <div class="success-notification-content">
            <div class="success-notification-icon">✓</div>
            <div class="success-notification-text">{{ successNotificationText }}</div>
            <button type="button" class="success-notification-close" @click="hideSuccessNotification">×</button>
          </div>
        </div>
      </transition>

      <transition name="fade">
        <div class="notice-overlay" v-if="notice">
          <div class="notice-card" :class="`notice-card--${noticeType}`">
            <div class="notice-title">
              {{ noticeType === 'success' ? 'Готово' : 'Сообщение' }}
            </div>
            <p>{{ notice }}</p>
            <button type="button" @click="closeNotice">Закрыть</button>
          </div>
        </div>
      </transition>

      <div class="page-title">
        <div class="page-back" @click="$router.go(-1)">
          <svg style="width:24px;height:24px" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M10.05 16.94V12.94H18.97L19 10.93H10.05V6.94L5.05 11.94Z"
            />
          </svg>
        </div>
        СПАСИБО
      </div>

      <div class="hero-card">
        <div class="hero-text">
          <div class="hero-badge">Виртуальный магазин</div>
          <h3>Добро пожаловать в корпоративный магазин «Садко»</h3>
          <p>
            Здесь можно потратить накопленные СПАСИБО на подарки.
            Выберите товар, при необходимости укажите размер или цвет
            и оформите заказ в пару касаний.
          </p>
        </div>

        <div class="balance-card">
          <span>Доступно</span>
          <strong>{{ amount }}</strong>
          <small>СПАСИБО</small>
        </div>
      </div>

      <div class="toolbar-card">
        <div class="toolbar-title">Сортировка</div>
        <div class="toolbar-actions">
          <button
            type="button"
            class="sort-chip"
            :class="{ active: order === 'none' }"
            @click="order = 'none'"
          >
            Новее
          </button>

          <button
            type="button"
            class="sort-chip"
            :class="{ active: order === 'asc' }"
            @click="order = 'asc'"
          >
            Дешевле
          </button>

          <button
            type="button"
            class="sort-chip"
            :class="{ active: order === 'desc' }"
            @click="order = 'desc'"
          >
            Дороже
          </button>
        </div>
      </div>

      <div class="section-card" v-if="giftsWait.length">
        <div class="section-head">
          <div>
            <div class="section-title">Мои заказы</div>
            <div class="section-subtitle">
              Подарки, которые уже оформлены и ожидают выдачи
            </div>
          </div>
          <div class="section-counter">{{ giftsWait.length }}</div>
        </div>

        <div class="orders-scroll">
          <div class="order-card" v-for="it in giftsWait" :key="it.id">
            <div class="order-media">
              <ProductImage :id="it.id" />
            </div>

            <div class="order-body">
              <div class="order-name">{{ it.name }}</div>
              <div class="order-status">Заказ оформлен</div>
            </div>
          </div>
        </div>
      </div>

      <div class="section-card">
        <div class="section-head">
          <div>
            <div class="section-title">Каталог подарков</div>
            <div class="section-subtitle">
              Выберите подарок и оформите заказ
            </div>
          </div>
          <div class="section-counter">{{ gifts.length }}</div>
        </div>

        <div class="empty-state" v-if="!gifts.length">
          <div class="empty-icon">🎁</div>
          <div class="empty-title">Сейчас нет доступных подарков</div>
          <div class="empty-subtitle">
            Попробуйте изменить сортировку или загляните позже
          </div>
        </div>

        <div v-else class="product-list">
          <div class="product-card" v-for="it in gifts" :key="it.id">
            <div class="product-media">
              <ProductImage :id="it.id" />
            </div>

            <div class="product-body">
              <div class="product-header">
                <div class="product-title">{{ it.name }}</div>
                <div class="product-price">
                  {{ it.cost }} <span>СПАСИБО</span>
                </div>
              </div>

              <div class="product-desc">
                {{ it.description }}
              </div>

              <div class="product-option" v-if="it.hasSize">
                <label :for="`gift-size-${it.id}`">Размер / цвет</label>
                <input
                  :id="`gift-size-${it.id}`"
                  :value="getSizeComment(it.id)"
                  @input="setSizeComment(it.id, $event.target.value)"
                  placeholder="Например: L, 42, черный"
                />
              </div>

              <div class="product-footer">
                <div
                  class="product-hint"
                  v-if="giftMissingAmount(it) > 0"
                >
                  Не хватает {{ giftMissingAmount(it) }} СПАСИБО
                </div>

                <div class="product-hint product-hint--ok" v-else>
                  Доступен к заказу
                </div>

                <button
                  type="button"
                  class="buy-button"
                  :class="{
                    'buy-button--disabled':
                      giftMissingAmount(it) > 0 || buyingId === it.id
                  }"
                  :disabled="giftMissingAmount(it) > 0 || buyingId === it.id"
                  @click="buy(it)"
                >
                  <span v-if="buyingId === it.id">Оформляем...</span>
                  <span v-else-if="giftMissingAmount(it) > 0">
                    Недостаточно СПАСИБО
                  </span>
                  <span v-else>
                    Купить
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from "axios";
import ProductImage from "@/ProductImage.vue";

const baseUrl =
  process.env.NODE_ENV === "production"
    ? "/api"
    : "http://192.168.100.72:9090";

function sortByNewest(items) {
  return [...items].sort((a, b) => {
    if (a?.createdAt && b?.createdAt) {
      return new Date(b.createdAt) - new Date(a.createdAt);
    }
    return (Number(b?.id) || 0) - (Number(a?.id) || 0);
  });
}

export default {
  name: "ProductsPage",
  components: { ProductImage },

  data() {
    return {
      order: "none",
      loading: true,
      buyingId: null,
      notice: "",
      noticeType: "error",
      sizeComments: {},
      showConfirmDialog: false,
      selectedGift: null,
      showSuccessNotification: false,
      successNotificationText: "",
      notificationTimeout: null,
    };
  },

  metaInfo() {
    return {
      title: "СПАСИБО",
      meta: [
        {
          name: "viewport",
          content:
            "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0",
        },
      ],
    };
  },

  computed: {
    amount() {
      return Number(this.$store.getters.amount) || 0;
    },

    orders() {
      return this.$store.getters.orders || [];
    },

    profile() {
      return this.$store.getters.profile || {};
    },

    allGifts() {
      return (this.$store.getters.gifts || []).filter((it) => it && it.enabled);
    },

    pendingOrders() {
      return this.orders.filter((o) => Number(o.done) < 1);
    },

    pendingGiftIds() {
      return new Set(this.pendingOrders.map((o) => o.gift));
    },

    giftsWait() {
      return sortByNewest(
        this.allGifts.filter((it) => this.pendingGiftIds.has(it.id))
      );
    },

    gifts() {
      let result = this.allGifts.filter(
        (it) => !this.pendingGiftIds.has(it.id)
      );

      if (this.order === "asc") {
        return [...result].sort(
          (a, b) => (Number(a.cost) || 0) - (Number(b.cost) || 0)
        );
      }

      if (this.order === "desc") {
        return [...result].sort(
          (a, b) => (Number(b.cost) || 0) - (Number(a.cost) || 0)
        );
      }

      return sortByNewest(result);
    },
  },

  mounted() {
    this.loadOrders(true);
  },

  methods: {
    showNotice(message, type = "error") {
      this.notice = message;
      this.noticeType = type;
    },

    closeNotice() {
      this.notice = "";
    },

    getSizeComment(id) {
      return this.sizeComments[id] || "";
    },

    setSizeComment(id, value) {
      if (this.$set) {
        this.$set(this.sizeComments, id, value);
      } else {
        this.sizeComments = {
          ...this.sizeComments,
          [id]: value,
        };
      }
    },

    giftMissingAmount(gift) {
      return Math.max(0, (Number(gift.cost) || 0) - this.amount);
    },

    async loadOrders(showLoader = false) {
      if (showLoader) this.loading = true;

      try {
        if (!this.profile.id) {
          this.showNotice("Не найден профиль пользователя.");
          return;
        }

        const res = await axios.get(
          `${baseUrl}/thanks/orders/${this.profile.id}`
        );

        this.$store.commit("setOrders", res.data || []);
      } catch (e) {
        console.error("Ошибка загрузки заказов:", e);
        this.showNotice(
          "Не удалось загрузить заказы. Попробуйте обновить страницу.",
          "error"
        );
      } finally {
        if (showLoader) this.loading = false;
      }
    },

    async buy(gift) {
      if (this.buyingId !== null) return;

      const comment = this.getSizeComment(gift.id).trim();

      if (gift.hasSize && !comment) {
        this.showNotice("Введите желаемый размер или цвет.", "error");
        return;
      }

      // Показываем диалог подтверждения
      this.selectedGift = gift;
      this.showConfirmDialog = true;
    },

    cancelPurchase() {
      this.showConfirmDialog = false;
      this.selectedGift = null;
    },

    async confirmPurchase() {
      if (!this.selectedGift) return;

      this.showConfirmDialog = false;
      this.buyingId = this.selectedGift.id;

      try {
        const comment = this.getSizeComment(this.selectedGift.id).trim();

        await axios.post(
          `${baseUrl}/thanks/buy/${this.profile.id}/${this.selectedGift.id}`,
          comment,
          {
            headers: {
              "Content-Type": "text/plain;charset=UTF-8",
            },
          }
        );

        // Показываем уведомление об успешной покупке
        this.showSuccessNotificationMessage(`Подарок "${this.selectedGift.name}" успешно оформлен!`);
        this.setSizeComment(this.selectedGift.id, "");
        await this.loadOrders(false);
      } catch (e) {
        console.error("Ошибка покупки:", e);

        if (e.response) {
          if (e.response.status === 400) {
            this.showNotice(
              "Недостаточно СПАСИБО для покупки этого подарка",
              "error"
            );
          } else if (e.response.status === 404) {
            this.showNotice("Подарок не найден", "error");
          } else if (e.response.status === 409) {
            this.showNotice("Этот подарок уже был заказан", "error");
          } else if (e.response.data && e.response.data.message) {
            this.showNotice(e.response.data.message, "error");
          } else {
            this.showNotice(`Ошибка сервера: ${e.response.status}`, "error");
          }
        } else if (e.request) {
          this.showNotice(
            "Нет соединения с сервером. Проверьте подключение к интернету.",
            "error"
          );
        } else {
          this.showNotice(
            "Произошла ошибка при оформлении покупки. Попробуйте позже.",
            "error"
          );
        }
      } finally {
        this.buyingId = null;
        this.selectedGift = null;
      }
    },

    showSuccessNotificationMessage(message) {
      this.successNotificationText = message;
      this.showSuccessNotification = true;

      // Очищаем предыдущий таймаут, если есть
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout);
      }

      // Автоматически скрываем уведомление через 5 секунд
      this.notificationTimeout = setTimeout(() => {
        this.hideSuccessNotification();
      }, 5000);
    },

    hideSuccessNotification() {
      this.showSuccessNotification = false;
      this.successNotificationText = "";
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout);
        this.notificationTimeout = null;
      }
    },
  },
};
</script>

<style scoped>
.shop-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at top, rgba(140, 122, 230, 0.18), transparent 30%),
    #f4f7fb;
  color: #1f2937;
}

.page-list {
  max-width: 920px;
  margin: 0 auto;
  padding-bottom: 28px;
}

.page-title {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 18px;
  background: rgba(244, 247, 251, 0.92);
  backdrop-filter: blur(10px);
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
}

.page-back {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
  cursor: pointer;
  flex-shrink: 0;
}

.page-back svg {
  padding-top: 0;
  padding-right: 0;
}

.hero-card {
  margin: 16px;
  padding: 18px;
  border-radius: 24px;
  background: linear-gradient(135deg, #6c5ce7, #8c7ae6 55%, #74b9ff);
  color: #fff;
  box-shadow: 0 16px 34px rgba(108, 92, 231, 0.24);
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  margin-bottom: 12px;
}

.hero-text h3 {
  margin: 0 0 10px;
  font-size: 22px;
  line-height: 1.2;
}

.hero-text p {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.92);
}

.balance-card {
  margin-top: 18px;
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(10px);
  display: inline-flex;
  flex-direction: column;
  min-width: 170px;
}

.balance-card span {
  font-size: 12px;
  text-transform: uppercase;
  opacity: 0.9;
  letter-spacing: 0.08em;
}

.balance-card strong {
  font-size: 34px;
  line-height: 1.1;
  margin: 4px 0;
  font-weight: 800;
}

.balance-card small {
  font-size: 13px;
  opacity: 0.94;
}

.toolbar-card,
.section-card {
  margin: 16px;
  background: #fff;
  border-radius: 22px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.toolbar-card {
  padding: 16px;
}

.toolbar-title {
  font-size: 14px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 12px;
}

.toolbar-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.sort-chip {
  border: 0;
  outline: 0;
  cursor: pointer;
  border-radius: 999px;
  padding: 10px 14px;
  background: #f1f5f9;
  color: #334155;
  font-size: 13px;
  font-weight: 700;
  transition: all 0.2s ease;
}

.sort-chip.active {
  background: linear-gradient(135deg, #74b9ff, #6c5ce7);
  color: #fff;
  box-shadow: 0 8px 18px rgba(108, 92, 231, 0.22);
}

.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 18px 12px;
}

.section-title {
  font-size: 18px;
  font-weight: 800;
  color: #111827;
}

.section-subtitle {
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.4;
  color: #6b7280;
}

.section-counter {
  min-width: 36px;
  height: 36px;
  border-radius: 999px;
  background: #eef2ff;
  color: #5b21b6;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
  padding: 0 10px;
}

.orders-scroll {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 0 18px 18px;
  -webkit-overflow-scrolling: touch;
}

.orders-scroll::-webkit-scrollbar {
  display: none;
}

.order-card {
  min-width: 220px;
  max-width: 220px;
  border-radius: 18px;
  overflow: hidden;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.order-media {
  width: 100%;
  height: 130px;
  background: #eef2f7;
}

.order-body {
  padding: 12px;
}

.order-name {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  line-height: 1.35;
}

.order-status {
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(34, 197, 94, 0.12);
  color: #15803d;
  font-size: 12px;
  font-weight: 700;
}

.product-list {
  padding: 0 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.product-card {
  display: flex;
  gap: 14px;
  padding: 14px;
  border-radius: 20px;
  background: #fbfdff;
  border: 1px solid #e9eef5;
}

.product-media {
  width: 132px;
  min-width: 132px;
  height: 260px;
  border-radius: 16px;
  overflow: hidden;
  background: #eef2f7;
}

.product-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.product-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.product-title {
  font-size: 16px;
  font-weight: 800;
  line-height: 1.35;
  color: #111827;
}

.product-price {
  flex-shrink: 0;
  white-space: nowrap;
  padding: 8px 10px;
  border-radius: 12px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 13px;
  font-weight: 800;
}

.product-price span {
  font-size: 11px;
  opacity: 0.9;
}

.product-desc {
  margin-top: 10px;
  color: #667085;
  font-size: 13px;
  line-height: 1.45;
}

.product-option {
  margin-top: 12px;
}

.product-option label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
}

.product-option input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d6dbe4;
  background: #fff;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 14px;
  outline: none;
  transition: 0.2s ease;
}

.product-option input:focus {
  border-color: #8c7ae6;
  box-shadow: 0 0 0 4px rgba(140, 122, 230, 0.12);
}

.product-footer {
  margin-top: auto;
  padding-top: 14px;
}

.product-hint {
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 700;
  color: #dc2626;
}

.product-hint--ok {
  color: #16a34a;
}

.buy-button {
  width: 100%;
  border: 0;
  outline: 0;
  cursor: pointer;
  border-radius: 14px;
  padding: 12px 14px;
  background: linear-gradient(135deg, #6c5ce7, #8c7ae6);
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.01em;
  box-shadow: 0 10px 22px rgba(108, 92, 231, 0.2);
  transition: transform 0.15s ease, opacity 0.15s ease;
}

.buy-button:active {
  transform: scale(0.99);
}

.buy-button--disabled {
  background: #cbd5e1;
  box-shadow: none;
  cursor: not-allowed;
}

.empty-state {
  padding: 18px 18px 24px;
  text-align: center;
}

.empty-icon {
  font-size: 42px;
  margin-bottom: 8px;
}

.empty-title {
  font-size: 16px;
  font-weight: 800;
  color: #111827;
}

.empty-subtitle {
  margin-top: 6px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.45;
}

.notice-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(15, 23, 42, 0.56);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
}

.notice-card {
  width: 100%;
  max-width: 360px;
  border-radius: 22px;
  background: #fff;
  padding: 20px;
  text-align: center;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.22);
}

.notice-card--success {
  border-top: 5px solid #22c55e;
}

.notice-card--error {
  border-top: 5px solid #ef4444;
}

.notice-title {
  font-size: 18px;
  font-weight: 800;
  color: #111827;
  margin-bottom: 8px;
}

.notice-card p {
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.45;
  color: #475569;
}

.notice-card button {
  border: 0;
  outline: 0;
  cursor: pointer;
  border-radius: 12px;
  padding: 12px 16px;
  background: #111827;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  width: 100%;
}

.loading-screen {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #6c5ce7, #74b9ff);
  color: white;
}

.loading-text {
  margin-top: 14px;
  font-size: 15px;
  font-weight: 700;
  opacity: 0.96;
}

.spinner {
  width: 52px;
  height: 52px;
  position: relative;
  text-align: center;
  filter: url("#goo");
  -webkit-animation: sk-rotate 2s infinite linear;
  animation: sk-rotate 2s infinite linear;
}

.dot1,
.dot2 {
  width: 60%;
  height: 60%;
  display: inline-block;
  position: absolute;
  top: 0;
  background-color: #fff;
  border-radius: 100%;
  -webkit-animation: sk-bounce 2s infinite ease-in-out;
  animation: sk-bounce 2s infinite ease-in-out;
}

.dot2 {
  top: auto;
  bottom: 0;
  -webkit-animation-delay: -1s;
  animation-delay: -1s;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@-webkit-keyframes sk-rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes sk-rotate {
  100% {
    transform: rotate(360deg);
  }
}

/* Диалог подтверждения покупки */
.confirm-dialog-overlay {
  position: fixed;
  inset: 0;
  z-index: 1001;
  background: rgba(15, 23, 42, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
}

.confirm-dialog {
  width: 100%;
  max-width: 400px;
  border-radius: 24px;
  background: #fff;
  padding: 24px;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.25);
  animation: slideUp 0.25s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.confirm-dialog-header {
  text-align: center;
  margin-bottom: 20px;
}

.confirm-dialog-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.confirm-dialog-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #111827;
}

.confirm-dialog-body {
  margin-bottom: 24px;
}

.confirm-dialog-body p {
  margin: 0 0 16px;
  font-size: 15px;
  color: #475569;
  text-align: center;
}

.confirm-dialog-gift {
  padding: 16px;
  background: #f8fafc;
  border-radius: 16px;
  text-align: center;
}

.confirm-dialog-gift-name {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 6px;
}

.confirm-dialog-gift-price {
  font-size: 18px;
  font-weight: 800;
  color: #6c5ce7;
}

.confirm-dialog-size {
  margin-top: 12px;
  padding: 10px 14px;
  background: #e0e7ff;
  border-radius: 10px;
  font-size: 13px;
  color: #4338ca;
  font-weight: 600;
}

.confirm-dialog-footer {
  display: flex;
  gap: 12px;
}

.confirm-dialog-btn {
  flex: 1;
  border: 0;
  outline: 0;
  cursor: pointer;
  border-radius: 14px;
  padding: 14px 16px;
  font-size: 15px;
  font-weight: 700;
  transition: all 0.2s ease;
}

.confirm-dialog-btn--cancel {
  background: #f1f5f9;
  color: #475569;
}

.confirm-dialog-btn--cancel:hover {
  background: #e2e8f0;
}

.confirm-dialog-btn--confirm {
  background: linear-gradient(135deg, #6c5ce7, #74b9ff);
  color: #fff;
  box-shadow: 0 8px 18px rgba(108, 92, 231, 0.25);
}

.confirm-dialog-btn--confirm:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(108, 92, 231, 0.3);
}

/* Уведомление об успешной покупке */
.success-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1002;
  max-width: 380px;
  animation: slideInRight 0.3s ease;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.success-notification-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  border-radius: 16px;
  color: #fff;
  box-shadow: 0 12px 28px rgba(34, 197, 94, 0.3);
}

.success-notification-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 800;
}

.success-notification-text {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
}

.success-notification-close {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border: 0;
  outline: 0;
  cursor: pointer;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  font-size: 20px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
}

.success-notification-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

@media (max-width: 480px) {
  .success-notification {
    top: 10px;
    right: 10px;
    left: 10px;
    max-width: none;
  }
}

@-webkit-keyframes sk-bounce {
  0%, 100% {
    transform: scale(0);
  }
  50% {
    transform: scale(1);
  }
}

@keyframes sk-bounce {
  0%, 100% {
    transform: scale(0);
  }
  50% {
    transform: scale(1);
  }
}

.order-media ::v-deep img,
.product-media ::v-deep img,
.order-media ::v-deep picture,
.product-media ::v-deep picture,
.order-media ::v-deep .image,
.product-media .image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

@media (max-width: 640px) {
  .hero-card,
  .toolbar-card,
  .section-card {
    margin: 12px;
  }

  .page-title {
    padding: 14px 12px;
    font-size: 20px;
  }

  .section-head {
    padding: 16px 16px 10px;
  }

  .product-list {
    padding: 0 16px 16px;
  }

  .orders-scroll {
    padding: 0 16px 16px;
  }

  .product-card {
    flex-direction: column;
  }

  .product-media {
    width: 100%;
    min-width: 100%;
    height: 260px;
  }

  .product-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .product-price {
    margin-top: 2px;
  }

  .balance-card {
    width: 100%;
    box-sizing: border-box;
  }
}
</style>